import { onIdTokenChanged } from 'firebase/auth';
import { io, Socket } from 'socket.io-client';
import { auth, getFreshIdToken } from './firebase';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const SOCKET_BASE = API_BASE.endsWith('/api')
  ? API_BASE.slice(0, -4)
  : API_BASE || window.location.origin;
const TICKETS_REALTIME_ENABLED =
  String(process.env.REACT_APP_TICKETS_REALTIME_ENABLED ?? 'true').toLowerCase() !==
  'false';

export type TicketChangedPayload = {
  orgId: string;
  ticketId: string;
  eventType: string;
  eventId?: string | null;
  actorUserId?: string | null;
  actorFirebaseUid?: string | null;
  clientMutationId?: string | null;
  deleted?: boolean;
  changedAt?: string | null;
  data?: Record<string, any> | null;
};

let socket: Socket | null = null;
let unsubIdToken: (() => void) | null = null;
let started = false;
const listeners = new Set<(payload: TicketChangedPayload) => void>();

async function connect(force = false) {
  if (!TICKETS_REALTIME_ENABLED) return;
  let token: string | null = null;
  try {
    token = await getFreshIdToken();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[ticketsRealtime] token fetch failed', error);
    }
    return;
  }
  if (!token) return;
  if (!force && socket?.connected) return;

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(`${SOCKET_BASE}/notifications`, {
    path: '/socket.io',
    transports: ['websocket'],
    withCredentials: true,
    auth: { token },
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('ticket.changed', (payload: TicketChangedPayload) => {
    for (const listener of listeners) {
      try {
        listener(payload);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[ticketsRealtime] listener failed', error);
        }
      }
    }
  });

  socket.on('connect_error', (error: any) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[ticketsRealtime] connect_error', error?.message || error);
    }
  });
}

async function startCore() {
  if (started || !TICKETS_REALTIME_ENABLED) return;
  started = true;
  await connect(true);
  let skipInitialTokenEvent = true;
  unsubIdToken = onIdTokenChanged(auth, async (u) => {
    if (!started) return;
    if (skipInitialTokenEvent) {
      skipInitialTokenEvent = false;
      return;
    }
    if (!u) {
      stopCore();
      return;
    }
    await connect(true);
  });
}

function stopCore() {
  started = false;
  if (unsubIdToken) {
    unsubIdToken();
    unsubIdToken = null;
  }
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

export async function subscribeTicketsRealtime(
  listener: (payload: TicketChangedPayload) => void,
) {
  listeners.add(listener);
  await startCore();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      stopCore();
    }
  };
}
